import React, { useEffect, useMemo, useState } from "react";

import { api } from "@/lib/api";
import { getStoredUser } from "@/lib/auth";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import ConfirmAutocloseDialog from "@/components/commission/ConfirmAutocloseDialog";

import SingleSelectFilter from "@/components/leads/SingleSelectFilter";
import MultiSelectFilter from "@/components/leads/MultiSelectFilter";

export default function CommissionProfileDialog({
  open,
  onOpenChange,
  onSaved,
  title = "Add item",
  templatePlaceholder = false,
  showHouseAdvanceMonths = false,
  existingProfiles = [],
}) {
  const user = getStoredUser();
  const { toast } = useToast();

  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [uplines, setUplines] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [skipCompanyReset, setSkipCompanyReset] = useState(false);
  const [skipProductReset, setSkipProductReset] = useState(false);

  const [busy, setBusy] = useState(false);

  const [companyId, setCompanyId] = useState("");
  const [companyNewName, setCompanyNewName] = useState("");

  const [productId, setProductId] = useState("");
  const [productNewName, setProductNewName] = useState("");

  const [issueAgeValue, setIssueAgeValue] = useState("");
  const [issueAgeNewValue, setIssueAgeNewValue] = useState("");
  const [effectiveStartDate, setEffectiveStartDate] = useState("");
  const [effectiveEndDate, setEffectiveEndDate] = useState("");

  const [confirmAutocloseOpen, setConfirmAutocloseOpen] = useState(false);
  const [pendingAutoclose, setPendingAutoclose] = useState(null);
  const [pendingConflicts, setPendingConflicts] = useState([]);
  const [agentIds, setAgentIds] = useState([]);
  const [uplineIds, setUplineIds] = useState([]);

  const [agentCommissionPercent, setAgentCommissionPercent] = useState("");
  const [houseCommissionPercent, setHouseCommissionPercent] = useState("");
  const [agentAdvMonths, setAgentAdvMonths] = useState("");
  const [houseAdvMonths, setHouseAdvMonths] = useState("");

  const parseNumeric = (value) => {
    const raw = String(value ?? "").trim();
    if (!raw) return null;
    const cleaned = raw.replace(/[^0-9.-]/g, "");
    if (!cleaned) return null;
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : null;
  };

  const toInputValue = (val) => {
    if (val === null || val === undefined) return "";
    return String(val);
  };

  const loadCompanies = async () => {
    const res = await api.get("/commission/companies");
    setCompanies(res.data || []);
  };

  const loadProducts = async (cid) => {
    if (!cid || cid === "__new__") {
      setProducts([]);
      return;
    }
    const res = await api.get(`/commission/companies/${cid}/products`);
    setProducts(res.data || []);
  };

  const loadAgents = async () => {
    const res = await api.get("/commission/agents");
    setAgents(res.data || []);
  };

  const loadUplines = async () => {
    const res = await api.get("/admin/users");
    const eligible = (res.data || []).filter((u) =>
      ["agent", "admin", "super_admin"].includes(u.role) && u.enabled !== false
    );
    setUplines(eligible);
  };

  const reset = () => {
    setCompanyId("");
    setCompanyNewName("");
    setProductId("");
    setProductNewName("");
    setIssueAgeValue("");
    setIssueAgeNewValue("");
    setEffectiveStartDate("");
    setEffectiveEndDate("");
    setAgentIds([]);
    setUplineIds([]);
    setAgentCommissionPercent("");
    setHouseCommissionPercent("");
    setAgentAdvMonths("");
    setHouseAdvMonths("");
    setHouseAdvMonths("");
    setTemplateId("");
    setSkipCompanyReset(false);
    setSkipProductReset(false);
  };

  useEffect(() => {
    if (!open) return;
    reset();

    // defaults
    if (user?.list_as_sales_agent) {
      setAgentIds([user?.id].filter(Boolean));
    }

    loadCompanies();
    loadAgents();
    loadUplines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    loadProducts(companyId);
    if (skipCompanyReset) {
      setSkipCompanyReset(false);
      return;
    }
    setProductId("");
    setProductNewName("");
    setIssueAgeValue("");
    setIssueAgeNewValue("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, open, skipCompanyReset]);

  useEffect(() => {
    if (!open) return;
    if (skipProductReset) {
      setSkipProductReset(false);
      return;
    }
    setIssueAgeValue("");
    setIssueAgeNewValue("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, open, skipProductReset]);

  const companyOptions = useMemo(() => {
    const opts = (companies || []).map((c) => ({ value: c.id, label: c.name }));
    return [{ value: "__new__", label: "+ New" }, ...opts];
  }, [companies]);

  const productOptions = useMemo(() => {
    const opts = (products || []).map((p) => ({ value: p.id, label: p.name }));
    return [{ value: "__new__", label: "+ New" }, ...opts];
  }, [products]);

  const issueAgeOptions = useMemo(() => {
    const values = new Set();
    if (companyId && productId && companyId !== "__new__" && productId !== "__new__") {
      (existingProfiles || []).forEach((p) => {
        if (p.company_id === companyId && p.product_id === productId && p.issue_age) {
          values.add(String(p.issue_age));
        }
      });
    }
    const opts = Array.from(values)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      .map((v) => ({ value: v, label: v }));
    return [{ value: "__new__", label: "+ New" }, ...opts];
  }, [companyId, productId, existingProfiles]);

  const agentOptions = useMemo(() => {
    return (agents || [])
      .map((a) => {
        const name = String(a.name || "").trim();
        const email = String(a.email || "").trim();
        const label = name && email ? `${name} (${email})` : name || email || a.id;
        return { value: a.id, label };
      })
      .sort((x, y) => String(x.label).localeCompare(String(y.label), undefined, { sensitivity: "base" }));
  }, [agents]);

  const uplineOptions = useMemo(() => {
    return (uplines || [])
      .map((u) => {
        const name = String(u.name || "").trim();
        const email = String(u.email || "").trim();
        const label = name && email ? `${name} (${email})` : name || email || u.id;
        return { value: u.id, label };
      })
      .sort((x, y) => String(x.label).localeCompare(String(y.label), undefined, { sensitivity: "base" }));
  }, [uplines]);

  const templateOptions = useMemo(() => {
    if (!templatePlaceholder) return [];
    const byAgentId = {};
    (agentOptions || []).forEach((a) => (byAgentId[a.value] = a.label));
    const opts = (existingProfiles || []).map((p) => {
      const companyLabel = p.company_name || p.company_id || "Company";
      const productLabel = p.product_name || p.product_id || "Product";
      const issueAgeLabel = p.issue_age || "Issue Age";
      const agentLabel = byAgentId[p.agent_id] || p.agent_id || "Agent";
      const startLabel = p.effective_start_date ? String(p.effective_start_date).slice(0, 10) : "";
      const suffix = startLabel ? ` (Start ${startLabel})` : "";
      return {
        value: p.id,
        label: `${companyLabel} • ${productLabel} • ${issueAgeLabel} — ${agentLabel}${suffix}`,
      };
    });
    opts.sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { sensitivity: "base" }));
    return [{ value: "__none__", label: "No template" }, ...opts];
  }, [agentOptions, existingProfiles, templatePlaceholder]);

  const agentLabels = useMemo(() => {
    const byId = {};
    (agentOptions || []).forEach((a) => (byId[a.value] = a.label));
    return agentIds.map((id) => byId[id]).filter(Boolean);
  }, [agentIds, agentOptions]);

  const agentIdsByLabel = useMemo(() => {
    const m = new Map();
    (agentOptions || []).forEach((a) => m.set(a.label, a.value));
    return m;
  }, [agentOptions]);

  const uplineLabels = useMemo(() => {
    const byId = {};
    (uplineOptions || []).forEach((u) => (byId[u.value] = u.label));
    return uplineIds.map((id) => byId[id]).filter(Boolean);
  }, [uplineIds, uplineOptions]);

  const uplineIdsByLabel = useMemo(() => {
    const m = new Map();
    (uplineOptions || []).forEach((u) => m.set(u.label, u.value));
    return m;
  }, [uplineOptions]);

  useEffect(() => {
    if (!templatePlaceholder) return;
    if (!templateId) return;
    const selected = (existingProfiles || []).find((p) => p.id === templateId);
    if (!selected) return;
    setSkipCompanyReset(true);
    setSkipProductReset(true);
    setAgentIds(selected.agent_id ? [selected.agent_id] : []);
    setCompanyId(selected.company_id || "");
    setCompanyNewName("");
    setProductId(selected.product_id || "");
    setProductNewName("");
    setIssueAgeValue(toInputValue(selected.issue_age));
    setIssueAgeNewValue("");
    setUplineIds(Array.isArray(selected.upline_ids) ? selected.upline_ids : []);
    setEffectiveStartDate(toInputValue(selected.effective_start_date).slice(0, 10));
    setEffectiveEndDate(toInputValue(selected.effective_end_date).slice(0, 10));
    setAgentCommissionPercent(toInputValue(selected.default_agent_commission_percent));
    setHouseCommissionPercent(toInputValue(selected.house_commission_percent));
    setAgentAdvMonths(toInputValue(selected.default_agent_advance_months));
    setHouseAdvMonths(toInputValue(selected.house_advance_months));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, templatePlaceholder, existingProfiles]);

  // (save removed; submit handler below performs create-new company/product then saves profile)
  const createCompanyIfNeeded = async () => {
    if (companyId !== "__new__") return companyId;
    const name = companyNewName.trim();
    if (!name) return "";
    const res = await api.post("/commission/companies", { name });
    const cid = res.data?.id;
    await loadCompanies();
    return cid;
  };

  const createProductIfNeeded = async (cid) => {
    if (productId !== "__new__") return productId;
    const name = productNewName.trim();
    if (!name) return "";
    const res = await api.post(`/commission/companies/${cid}/products`, { name });
    const pid = res.data?.id;
    await loadProducts(cid);
    return pid;
  };

  const onCompanyChange = async (v) => {
    setCompanyId(v);
    if (v !== "__new__") {
      setCompanyNewName("");
    }
  };

  const onProductChange = async (v) => {
    setProductId(v);
    if (v !== "__new__") {
      setProductNewName("");
    }
  };

  const onSubmit = async () => {
    setBusy(true);
    try {
      let cid = companyId;
      if (cid === "__new__") {
        cid = await createCompanyIfNeeded();
        if (!cid) return;
        setCompanyId(cid);
      }

      let pid = productId;
      if (pid === "__new__") {
        pid = await createProductIfNeeded(cid);
        if (!pid) return;
        setProductId(pid);
      }

      const buildPayload = (agentId) => ({
        company_id: cid,
        product_id: pid,
        issue_age: issueAgeText,
        agent_id: agentId,
        upline_ids: uplineIds,
        effective_start_date: effectiveStartDate,
        effective_end_date: effectiveEndDate || null,
        default_agent_commission_percent: parseNumeric(agentCommissionPercent),
        default_agent_advance_months: parseNumeric(agentAdvMonths),
        house_commission_percent: parseNumeric(houseCommissionPercent),
        house_advance_months: parseNumeric(houseAdvMonths),
        agent_overrides: [],
      });

      try {
        const results = await Promise.all(agentIds.map((agentId) => api.post("/commission/profiles", buildPayload(agentId))));
        
        // Calculate total recalculated clients
        const totalRecalculated = results.reduce((sum, res) => sum + (res.data?.recalculated_clients_count || 0), 0);
        if (totalRecalculated > 0) {
          toast({
            title: "Commission Profile Created",
            description: `Recalculated commissions for ${totalRecalculated} client${totalRecalculated !== 1 ? 's' : ''}.`,
          });
        }
      } catch (e) {
        if (e?.response?.status === 409) {
          const detail = e?.response?.data?.detail || {};
          setPendingConflicts(detail.conflicts || []);
          setPendingAutoclose({ agentIds, buildPayload });
          setConfirmAutocloseOpen(true);
          return;
        }
        throw e;
      }

      onSaved?.();
    } finally {
      setBusy(false);
    }
  };

  const confirmAutoclose = async () => {
    if (!pendingAutoclose) return;
    setBusy(true);
    try {
      await Promise.all(
        pendingAutoclose.agentIds.map((agentId) =>
          api.post(`/commission/profiles?confirm_autoclose=true`, pendingAutoclose.buildPayload(agentId))
        )
      );
      toast({ title: "Saved", description: "Existing profile was auto-closed." });
      setConfirmAutocloseOpen(false);
      setPendingAutoclose(null);
      setPendingConflicts([]);
      onSaved?.();
    } catch (e) {
      toast({
        title: "Could not save",
        description: e?.response?.data?.detail?.message || e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const issueAgeText = issueAgeValue === "__new__" ? issueAgeNewValue.trim() : issueAgeValue.trim();
  const canSelectCompany = agentIds.length > 0;
  const canSelectProduct = Boolean(companyId) && (companyId !== "__new__" || companyNewName.trim());
  const canSelectIssueAge = Boolean(productId) && (productId !== "__new__" || productNewName.trim());
  const canSelectUpline = Boolean(issueAgeText);
  const canSubmit =
    companyId &&
    (companyId !== "__new__" || companyNewName.trim()) &&
    productId &&
    (productId !== "__new__" || productNewName.trim()) &&
    issueAgeText &&
    effectiveStartDate &&
    agentIds.length > 0 &&
    String(agentCommissionPercent).trim() !== "" &&
    String(agentAdvMonths).trim() !== "" &&
    String(houseCommissionPercent).trim() !== "" &&
    (!showHouseAdvanceMonths || String(houseAdvMonths).trim() !== "");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="glass max-h-[85vh] w-[95vw] overflow-y-auto border-white/10 text-slate-100 sm:max-w-[620px]"
        data-testid="commission-profile-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-base">{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Sales Agents */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">Sales Agent</div>
              <div className="text-xs text-slate-200/60">Required</div>
            </div>
            <MultiSelectFilter
              placeholder="Choose sales agent(s)"
              options={agentOptions.map((a) => a.label)}
              values={agentLabels}
              onChange={(labels) => {
                setAgentIds(labels.map((x) => agentIdsByLabel.get(x)).filter(Boolean));
              }}
              testIdPrefix="commission-agents"
            />
          </div>

          {templatePlaceholder ? (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">Use Template</div>
                <div className="text-xs text-slate-200/60">Optional</div>
              </div>
              <SingleSelectFilter
                placeholder="Choose a commission profile"
                options={templateOptions}
                value={templateId}
                onChange={(value) => {
                  if (value === "__none__") {
                    setTemplateId("");
                    return;
                  }
                  setTemplateId(value);
                }}
                testIdPrefix="commission-template"
              />
              <div className="text-xs text-slate-200/60" data-testid="commission-template-help">
                Selecting a template pre-fills the profile details. You can still edit any field.
              </div>
            </div>
          ) : null}

          {/* Company */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">Company</div>
              <div className="text-xs text-slate-200/60">Required</div>
            </div>
            <SingleSelectFilter
              placeholder="Choose company"
              options={companyOptions}
              value={companyId}
              onChange={onCompanyChange}
              testIdPrefix="commission-company"
              disabled={!canSelectCompany}
            />
            {companyId === "__new__" ? (
              <Input
                value={companyNewName}
                onChange={(e) => setCompanyNewName(e.target.value)}
                placeholder="New company name"
                className="mt-2 bg-white/5 text-slate-100 ring-1 ring-white/10"
                data-testid="commission-company-new"
              />
            ) : null}
          </div>

          {/* Product */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">Product</div>
              <div className="text-xs text-slate-200/60">Required</div>
            </div>
            <SingleSelectFilter
              placeholder="Choose product"
              options={productOptions}
              value={productId}
              onChange={onProductChange}
              testIdPrefix="commission-product"
              disabled={!canSelectProduct}
            />
            {productId === "__new__" ? (
              <Input
                value={productNewName}
                onChange={(e) => setProductNewName(e.target.value)}
                placeholder="New product name"
                className="mt-2 bg-white/5 text-slate-100 ring-1 ring-white/10"
                data-testid="commission-product-new"
              />
            ) : null}
          </div>

          {/* Issue Age */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">Issue Age</div>
              <div className="text-xs text-slate-200/60">Required</div>
            </div>
            <SingleSelectFilter
              placeholder="Choose issue age"
              options={issueAgeOptions}
              value={issueAgeValue}
              onChange={(v) => {
                setIssueAgeValue(v);
                if (v !== "__new__") setIssueAgeNewValue("");
              }}
              testIdPrefix="commission-issue-age"
              disabled={!canSelectIssueAge}
            />
            {issueAgeValue === "__new__" ? (
              <Input
                value={issueAgeNewValue}
                onChange={(e) => setIssueAgeNewValue(e.target.value)}
                placeholder="New issue age"
                className="mt-2 bg-white/5 text-slate-100 ring-1 ring-white/10"
                data-testid="commission-issue-age-new"
              />
            ) : null}
          </div>

          {/* Set Upline */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">Set Upline</div>
              <div className="text-xs text-slate-200/60">Optional</div>
            </div>
            <MultiSelectFilter
              placeholder="Select upline"
              options={uplineOptions.map((u) => u.label)}
              values={uplineLabels}
              onChange={(labels) => {
                setUplineIds(labels.map((x) => uplineIdsByLabel.get(x)).filter(Boolean));
              }}
              testIdPrefix="commission-upline"
              disabled={!canSelectUpline}
            />
          </div>

          {/* Profile Dates */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">Profile Start Date</div>
                <div className="text-xs text-slate-200/60">Required</div>
              </div>
              <Input
                type="date"
                value={effectiveStartDate}
                onChange={(e) => setEffectiveStartDate(e.target.value)}
                className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                data-testid="commission-effective-start"
              />
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">Profile End Date</div>
                <div className="text-xs text-slate-200/60">Optional</div>
              </div>
              <Input
                type="date"
                value={effectiveEndDate}
                onChange={(e) => setEffectiveEndDate(e.target.value)}
                className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                data-testid="commission-effective-end"
              />
            </div>
          </div>



          {/* Agent Commission % */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">Agent Commission %</div>
              <div className="text-xs text-slate-200/60">Required</div>
            </div>
            <Input
              value={agentCommissionPercent}
              onChange={(e) => setAgentCommissionPercent(e.target.value)}
              className="bg-white/5 text-slate-100 ring-1 ring-white/10"
              data-testid="commission-agent-commission"
            />
          </div>

          {/* House Commission % */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">House Commission %</div>
              <div className="text-xs text-slate-200/60">Required</div>
            </div>
            <Input
              value={houseCommissionPercent}
              onChange={(e) => setHouseCommissionPercent(e.target.value)}
              className="bg-white/5 text-slate-100 ring-1 ring-white/10"
              data-testid="commission-house-commission"
            />
          </div>

          {/* Agent Adv Months */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">Agent Adv Months</div>
              <div className="text-xs text-slate-200/60">Required</div>
            </div>
            <Input
              value={agentAdvMonths}
              onChange={(e) => setAgentAdvMonths(e.target.value)}
              className="bg-white/5 text-slate-100 ring-1 ring-white/10"
              data-testid="commission-agent-adv-months"
            />
          </div>

          {showHouseAdvanceMonths ? (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">House Adv Months</div>
                <div className="text-xs text-slate-200/60">Required</div>
              </div>
              <Input
                value={houseAdvMonths}
                onChange={(e) => setHouseAdvMonths(e.target.value)}
                className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                data-testid="commission-house-adv-months"
              />
            </div>
          ) : null}

        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            disabled={!canSubmit || busy}
            onClick={onSubmit}
            data-testid="commission-submit"
          >
            {busy ? "Creating & Recalculating..." : "Submit"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            data-testid="commission-cancel"
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>


      <ConfirmAutocloseDialog
        open={confirmAutocloseOpen}
        onOpenChange={setConfirmAutocloseOpen}
        conflicts={pendingConflicts}
        onConfirm={confirmAutoclose}
      />

    </Dialog>
  );
}
